/**
 * Complete Your Routine Slider
 * Vanilla JS implementation with slider, modal, and add-to-cart functionality
 */
class CompleteRoutineSlider {
  constructor(section) {
    this.section = section;
    this.wrapper = section.querySelector('.complete-routine-slider__wrapper');
    this.track = section.querySelector('.complete-routine-slider__track');
    this.prevBtn = section.querySelector('.complete-routine-slider__nav--prev');
    this.nextBtn = section.querySelector('.complete-routine-slider__nav--next');
    this.modal = document.getElementById(`complete-routine-modal-${section.dataset.sectionId}`);
    this.cards = section.querySelectorAll('.complete-routine-card');
    
    // Slider state
    this.currentIndex = 0;
    this.slideWidth = 0;
    this.slidesPerView = 1;
    this.slideSpacing = 20;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.autoplayInterval = null;
    
    // Modal state
    this.currentProduct = null;
    
    this.init();
  }

  init() {
    if (!this.track || !this.wrapper) return;
    
    this.setupSlider();
    this.setupNavigation();
    this.setupTouchEvents();
    this.setupModal();
    this.setupAddToCart();
    this.setupQuantitySelector();
    this.updateSlideWidth();
    this.updateNavigationButtons();
    
    // Handle window resize
    window.addEventListener('resize', () => {
      this.updateSlideWidth();
      this.goToSlide(this.currentIndex);
      this.updateNavigationButtons();
    });
    
    // Setup autoplay if enabled
    const autoplayEnabled = this.wrapper.dataset.autoplay === 'true';
    if (autoplayEnabled) {
      const speed = parseInt(this.wrapper.dataset.autoplaySpeed) || 3000;
      this.startAutoplay(speed);
    }
  }

  setupSlider() {
    // Get responsive settings
    const mobile = parseInt(this.wrapper.dataset.slidesPerViewMobile) || 1;
    const tablet = parseInt(this.wrapper.dataset.slidesPerViewTablet) || 2;
    const desktop = parseInt(this.wrapper.dataset.slidesPerViewDesktop) || 3;
    this.mobileItemsPerView = parseFloat(this.wrapper.dataset.mobileItemsPerView) || 1.5;
    this.slideSpacing = parseInt(this.wrapper.dataset.slideSpacing) || 20;
    
    // Determine current viewport
    if (window.innerWidth >= 1024) {
      this.slidesPerView = desktop;
    } else if (window.innerWidth >= 750) {
      this.slidesPerView = tablet;
    } else {
      this.slidesPerView = mobile;
    }
  }

  updateSlideWidth() {
    if (!this.track || this.cards.length === 0) return;
    
    this.setupSlider();
    
    // On mobile, use mobile_items_per_view to calculate width
    if (window.innerWidth < 750) {
      this.cards.forEach((card) => {
        card.style.width = '';
      });
      // Calculate slide width for navigation purposes based on mobile_items_per_view
      const containerWidth = this.track.parentElement.offsetWidth;
      const mobileWidthPercentage = 100 / this.mobileItemsPerView;
      this.slideWidth = (containerWidth * mobileWidthPercentage / 100);
    } else {
      const containerWidth = this.track.parentElement.offsetWidth;
      const totalSpacing = (this.slidesPerView - 1) * this.slideSpacing;
      this.slideWidth = (containerWidth - totalSpacing) / this.slidesPerView;
      
      // Update card widths for tablet/desktop
      this.cards.forEach((card, index) => {
        card.style.width = `${this.slideWidth}px`;
      });
    }
  }

  getMaxIndex() {
    if (window.innerWidth < 750) {
      // On mobile, allow swiping to the last product
      return Math.max(0, this.cards.length - 1);
    } else {
      return Math.max(0, this.cards.length - this.slidesPerView);
    }
  }

  setupNavigation() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  nextSlide() {
    const maxIndex = this.getMaxIndex();
    if (this.currentIndex < maxIndex) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  goToSlide(index) {
    if (!this.track || this.cards.length === 0) return;
    
    const maxIndex = this.getMaxIndex();
    this.currentIndex = Math.max(0, Math.min(index, maxIndex));
    
    const step = this.slideWidth + this.slideSpacing;
    const offset = -(this.currentIndex * step);
    this.track.style.transform = `translateX(${offset}px)`;
    
    this.updateNavigationButtons();
  }

  updateNavigationButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    const maxIndex = this.getMaxIndex();
    
    this.prevBtn.disabled = this.currentIndex === 0;
    this.nextBtn.disabled = this.currentIndex >= maxIndex;
  }

  setupTouchEvents() {
    if (!this.track) return;
    
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    
    // Touch events
    this.track.addEventListener('touchstart', (e) => {
      if (!e.touches || !e.touches.length) return;
      startX = e.touches[0].clientX;
      currentX = startX;
      isDragging = true;
      this.track.classList.add('complete-routine-slider__track--dragging');
    }, { passive: true });
    
    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging || !e.touches || !e.touches.length) return;
      currentX = e.touches[0].clientX;
      const diff = currentX - startX;
      const step = this.slideWidth + this.slideSpacing;
      const maxIndex = this.getMaxIndex();
      const currentOffset = -(this.currentIndex * step);
      const minOffset = -(maxIndex * step);
      const newOffset = Math.max(minOffset, Math.min(0, currentOffset + diff));
      this.track.style.transform = `translateX(${newOffset}px)`;
    }, { passive: true });
    
    this.track.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      this.track.classList.remove('complete-routine-slider__track--dragging');
      
      const diff = currentX - startX;
      const threshold = 50;
      
      if (Math.abs(diff) > threshold) {
        if (diff < 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      } else {
        this.goToSlide(this.currentIndex);
      }
    }, { passive: true });
    
    // Mouse drag events
    this.track.addEventListener('mousedown', (e) => {
      startX = e.clientX;
      currentX = startX;
      isDragging = true;
      this.track.classList.add('complete-routine-slider__track--dragging');
      e.preventDefault();
    });
    
    this.track.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      currentX = e.clientX;
      const diff = currentX - startX;
      const step = this.slideWidth + this.slideSpacing;
      const maxIndex = this.getMaxIndex();
      const currentOffset = -(this.currentIndex * step);
      const minOffset = -(maxIndex * step);
      const newOffset = Math.max(minOffset, Math.min(0, currentOffset + diff));
      this.track.style.transform = `translateX(${newOffset}px)`;
    });
    
    this.track.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      this.track.classList.remove('complete-routine-slider__track--dragging');
      
      const diff = currentX - startX;
      const threshold = 50;
      
      if (Math.abs(diff) > threshold) {
        if (diff < 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
      } else {
        this.goToSlide(this.currentIndex);
      }
    });
    
    this.track.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        this.track.classList.remove('complete-routine-slider__track--dragging');
        this.goToSlide(this.currentIndex);
      }
    });
  }

  startAutoplay(speed) {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      const maxIndex = Math.max(0, this.cards.length - this.slidesPerView);
      if (this.currentIndex >= maxIndex) {
        this.goToSlide(0);
      } else {
        this.nextSlide();
      }
    }, speed);
    
    // Pause on hover
    this.wrapper.addEventListener('mouseenter', () => this.stopAutoplay());
    this.wrapper.addEventListener('mouseleave', () => this.startAutoplay(speed));
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  setupModal() {
    if (!this.modal) return;
    
    const overlay = this.modal.querySelector('.complete-routine-modal__overlay');
    const closeBtn = this.modal.querySelector('.complete-routine-modal__close');
    const viewMoreBtns = this.section.querySelectorAll('.complete-routine-card__btn-view');
    
    // Open modal on "View more" click
    viewMoreBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const card = btn.closest('.complete-routine-card');
        if (card) {
          // copyCardRatingToModal niet meer nodig - reviews verwijderd uit modal
          this.openModal(card);
        }
      });
    });
    
    // Close modal
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeModal());
    }
    
    if (overlay) {
      overlay.addEventListener('click', () => this.closeModal());
    }
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  async openModal(card) {
    if (!this.modal || !card) return;
    
    const productHandle = card.dataset.productHandle;
    if (!productHandle) return;
    
    // Store card data for modal
    this.currentCard = card;
    
    // Show loading state
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    try {
      // Fetch product data
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product not found');
      
      const product = await response.json();
      this.currentProduct = product;
      
      // Get first available variant
      const variant = product.variants.find(v => v.available) || product.variants[0];
      if (!variant) return;
      
      // Update modal content
      this.updateModalContent(product, variant, card);
      
    } catch (error) {
      console.error('Error loading product:', error);
      this.closeModal();
    }
  }

  copyCardRatingToModal(card) {
    if (!this.modal || !card) return;
    const cardStars = card.querySelector('.complete-routine-card__stars');
    const cardReviewCount = card.querySelector('.complete-routine-card__review-count');
    const modalStars = this.modal.querySelector('.complete-routine-modal__stars');
    const modalReviewCount = this.modal.querySelector('.complete-routine-modal__review-count');

    if (modalStars) {
      modalStars.innerHTML = cardStars ? cardStars.innerHTML : '';
    }

    if (modalReviewCount) {
      modalReviewCount.textContent = cardReviewCount ? cardReviewCount.textContent : '';
    }
  }

  updateModalContent(product, variant, card) {
    if (!this.modal) return;
    
    // Image - use custom image if available, otherwise product featured image
    const imageEl = this.modal.querySelector('.complete-routine-modal__image');
    const imageWrapper = this.modal.querySelector('.complete-routine-modal__image-wrapper');
    const customImage = card.dataset.customImage;
    const imageUrl = customImage && customImage !== '' ? customImage : (product.featured_image || '');
    
    if (imageEl && imageUrl) {
      imageEl.src = imageUrl;
      imageEl.alt = product.title;
      imageEl.style.display = 'block';
    } else if (imageWrapper) {
      imageWrapper.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;">No image</div>';
    }
    
    // Title
    const titleEl = this.modal.querySelector('.complete-routine-modal__title');
    if (titleEl) {
      titleEl.textContent = product.title;
    }
    
    // Price - verwijderd voor modal
    // Rating - verwijderd voor modal
    
    // Description - use modal_description from card data
    const descEl = this.modal.querySelector('.complete-routine-modal__description');
    if (descEl && card) {
      const modalDescription = card.dataset.modalDescription;
      if (modalDescription && modalDescription.trim() !== '') {
        // Convert line breaks to paragraphs
        const paragraphs = modalDescription.split('\n').filter(p => p.trim() !== '');
        if (paragraphs.length > 0) {
          descEl.innerHTML = paragraphs.map(p => `<p>${p.trim()}</p>`).join('');
        } else {
          descEl.innerHTML = '';
        }
      } else {
        descEl.innerHTML = '';
      }
    }
    
    // Add to cart button
    const addToCartBtn = this.modal.querySelector('.complete-routine-modal__add-to-cart');
    const addToCartPrice = this.modal.querySelector('.complete-routine-modal__add-to-cart-price');
    if (addToCartBtn) {
      addToCartBtn.dataset.variantId = variant.id;
      addToCartBtn.dataset.productHandle = product.handle;
    }
    if (addToCartPrice) {
      const price = this.formatMoney(variant.price);
      addToCartPrice.textContent = price;
    }
    
    // Reset quantity
    const qtyInput = this.modal.querySelector('.complete-routine-modal__qty-input');
    if (qtyInput) {
      qtyInput.value = 1;
    }
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    this.currentProduct = null;
    
    // Rating clearing verwijderd - reviews niet meer in modal
  }

  setupQuantitySelector() {
    if (!this.modal) return;
    
    const minusBtn = this.modal.querySelector('.complete-routine-modal__qty-btn--minus');
    const plusBtn = this.modal.querySelector('.complete-routine-modal__qty-btn--plus');
    const input = this.modal.querySelector('.complete-routine-modal__qty-input');
    
    if (minusBtn && input) {
      minusBtn.addEventListener('click', () => {
        const current = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        const step = parseInt(input.step) || 1;
        const newValue = Math.max(min, current - step);
        input.value = newValue;
        this.updateModalPrice();
      });
    }
    
    if (plusBtn && input) {
      plusBtn.addEventListener('click', () => {
        const current = parseInt(input.value) || 1;
        const max = parseInt(input.max) || 10;
        const step = parseInt(input.step) || 1;
        const newValue = Math.min(max, current + step);
        input.value = newValue;
        this.updateModalPrice();
      });
    }
    
    if (input) {
      input.addEventListener('change', () => {
        const value = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 10;
        input.value = Math.max(min, Math.min(max, value));
        this.updateModalPrice();
      });
    }
  }

  updateModalPrice() {
    if (!this.currentProduct) return;
    
    const input = this.modal.querySelector('.complete-routine-modal__qty-input');
    const addToCartPrice = this.modal.querySelector('.complete-routine-modal__add-to-cart-price');
    const variant = this.currentProduct.variants.find(v => v.available) || this.currentProduct.variants[0];
    
    if (input && addToCartPrice && variant) {
      const quantity = parseInt(input.value) || 1;
      const totalPrice = variant.price * quantity;
      const formattedPrice = this.formatMoney(totalPrice);
      addToCartPrice.textContent = formattedPrice;
    }
  }

  setupAddToCart() {
    // Card add to cart buttons
    const cardButtons = this.section.querySelectorAll('.complete-routine-card__btn-cart');
    cardButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId, 1, btn);
        }
      });
    });
    
    // Modal add to cart button
    if (this.modal) {
      const modalBtn = this.modal.querySelector('.complete-routine-modal__add-to-cart');
      if (modalBtn) {
        modalBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = modalBtn.dataset.variantId;
          const input = this.modal.querySelector('.complete-routine-modal__qty-input');
          const quantity = input ? parseInt(input.value) || 1 : 1;
          if (variantId) {
            this.addToCart(variantId, quantity, modalBtn);
          }
        });
      }
    }
  }

  async addToCart(variantId, quantity, button) {
    if (!variantId || !button) return;
    
    // Prevent multiple clicks
    if (button.disabled || button.classList.contains('loading')) return;
    
    button.disabled = true;
    button.classList.add('loading');
    
    try {
      // Get cart drawer if available
      const cart = document.querySelector('cart-drawer, main-cart-drawer, #CartDrawer');
      const sections = cart && typeof cart.getSectionsToRender === 'function' 
        ? cart.getSectionsToRender().map(s => s.id) 
        : [];
      
      // Prepare request
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      if (sections.length > 0) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      
      // Get fetch config
      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      };
      
      // Merge with theme config if available
      if (typeof NextSkyTheme !== 'undefined' && NextSkyTheme.fetchConfig) {
        const themeConfig = NextSkyTheme.fetchConfig('javascript');
        if (themeConfig) {
          Object.assign(config.headers, themeConfig.headers || {});
          if (themeConfig.credentials) {
            config.credentials = themeConfig.credentials;
          }
        }
      }
      
      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      const response = await fetch(cartAddUrl, config);
      const data = await response.json();
      
      if (data.status) {
        // Error
        throw new Error(data.description || 'Failed to add to cart');
      }
      
      // Success
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(data);
        if (typeof eventModal !== 'undefined') {
          eventModal(cart, 'open', false, 'delay');
        }
      }
      
      // Show toast if enabled
      const showToast = this.section.dataset.addToCartToast !== 'false';
      if (showToast && typeof notifier !== 'undefined') {
        notifier.show('Product added to cart', 'success', 3000);
      }
      
      // Close modal if open
      if (this.modal && this.modal.classList.contains('active')) {
        this.closeModal();
      }
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (typeof notifier !== 'undefined') {
        notifier.show(error.message || 'Failed to add product to cart', 'error', 4000);
      }
    } finally {
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    // Fallback
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }
}

// Initialize all sliders
function initCompleteRoutineSliders() {
  const sections = document.querySelectorAll('.complete-routine-slider[data-section-id]');
  sections.forEach(section => {
    new CompleteRoutineSlider(section);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCompleteRoutineSliders);
} else {
  initCompleteRoutineSliders();
}

// Re-initialize for theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.complete-routine-slider[data-section-id]');
    if (section) {
      new CompleteRoutineSlider(section);
    }
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    initCompleteRoutineSliders();
  });
}

